import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';
import {
  ProjectReportsService,
  ProjectReportItem,
  ProjectReportParams,
} from '../../core/services/project-reports.service';
import {
  CustomerProjectsService,
  CustomerOption,
  ProjectOption,
  ActivityOption,
} from '../../core/services/customer-projects.service';

type GroupMode = 'KU' | 'P' | 'A' | 'K';

interface HeaderColumn {
  label: string;
  key: string;
  type: 'string' | 'number' | 'date';
}

function applyWorksheetFormats(
  worksheet: XLSX.WorkSheet,
  headers: HeaderColumn[],
  rowCount: number
): void {
  for (let row = 1; row <= rowCount; row++) {
    headers.forEach((header, col) => {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];
      if (!cell || cell.v === '' || cell.v === null || cell.v === undefined) {
        return;
      }

      if (header.type === 'date') {
        const dateParts = String(cell.v).split('-');
        if (dateParts.length === 3) {
          cell.t = 'd';
          cell.v = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
          cell.z = 'yyyy-mm-dd';
        }
        return;
      }

      if (header.type === 'number') {
        const numValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
        if (!Number.isNaN(numValue)) {
          cell.t = 'n';
          cell.v = numValue;
          cell.z = Number.isInteger(numValue) ? '0' : '0.00';
        }
      }
    });
  }
}

@Component({
  selector: 'app-ng-project-reports',
  template: require('./ng-project-reports.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-project-reports.component.scss').default],
  standalone: false,
})
export class NgProjectReportsComponent implements OnInit {
  // Date range
  dateFrom: moment.Moment = moment().startOf('month');
  dateTo: moment.Moment = moment().endOf('month');

  // Modes
  groupMode: GroupMode = 'K';
  custMode = { external: true, internal: true, absense: true };

  // Select options
  customers: CustomerOption[] = [];
  selectedCustomer: CustomerOption | null = null;
  selectedProject: ProjectOption | null = null;
  selectedActivity: ActivityOption | null = null;

  // Data
  reportData: ProjectReportItem[] = [];
  headers: HeaderColumn[] = [];

  // Sorting
  private sortAscending = true;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(ProjectReportsService) private projectReportsService: ProjectReportsService,
    @Inject(CustomerProjectsService) private customerProjectsService: CustomerProjectsService
  ) {}

  ngOnInit(): void {
    this.fetchCustomerProjects();
  }

  fetchCustomerProjects(): void {
    this.customerProjectsService.getCustomerProjects().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.customers = data;
    });
  }

  onCustomerChange(): void {
    this.selectedProject = null;
    this.selectedActivity = null;
  }

  onProjectChange(): void {
    this.selectedActivity = null;
  }

  get projects(): ProjectOption[] {
    return this.selectedCustomer?.projects ?? [];
  }

  get activities(): ActivityOption[] {
    return this.selectedProject?.activities ?? [];
  }

  updatePeriod(direction: number): void {
    if (direction === -1) {
      this.dateFrom = moment(this.dateFrom).subtract(1, 'month').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    } else {
      this.dateFrom = moment(this.dateFrom).add(1, 'month').startOf('month');
      this.dateTo = moment(this.dateFrom).endOf('month');
    }
  }

  fetchReport(): void {
    this.updateHeaders();

    const params: ProjectReportParams = {
      id: 0,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
      type: this.groupMode,
      per: 'A',
      custMode: this.custMode,
      cust: this.selectedCustomer?.id ?? null,
      proj: this.selectedProject?.id ?? null,
      act: this.selectedActivity?.id ?? null,
    };

    this.projectReportsService.getProjectReport(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.reportData = data;
    });
  }

  private updateHeaders(): void {
    switch (this.groupMode) {
      case 'KU':
        this.headers = [
          { label: 'Namn', key: 'name', type: 'string' },
          { label: 'Kund', key: 'customername', type: 'string' },
          { label: 'Summa', key: 'sum', type: 'number' },
        ];
        break;
      case 'P':
        this.headers = [
          { label: 'Namn', key: 'name', type: 'string' },
          { label: 'Kund', key: 'customername', type: 'string' },
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Summa', key: 'sum', type: 'number' },
        ];
        break;
      case 'A':
        this.headers = [
          { label: 'Namn', key: 'name', type: 'string' },
          { label: 'Kund', key: 'customername', type: 'string' },
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Aktivitet', key: 'activity_name', type: 'string' },
          { label: 'Ext aktivitet', key: 'external_activity_name', type: 'string' },
          { label: 'Summa', key: 'sum', type: 'number' },
        ];
        break;
      case 'K':
        this.headers = [
          { label: 'Namn', key: 'name', type: 'string' },
          { label: 'Datum', key: 'reporteddate', type: 'date' },
          { label: 'Kund', key: 'customername', type: 'string' },
          { label: 'Projekt', key: 'project_name', type: 'string' },
          { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
          { label: 'Aktivitet', key: 'activity_name', type: 'string' },
          { label: 'Ext aktivitet', key: 'external_activity_name', type: 'string' },
          { label: 'Timmar', key: 'time', type: 'number' },
        ];
        break;
    }
  }

  sortBy(index: number): void {
    const header = this.headers[index];
    const key = header.key as keyof ProjectReportItem;

    this.reportData.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison: number;
      if (header.type === 'number') {
        comparison = (aVal as number) - (bVal as number);
      } else {
        comparison = String(aVal).localeCompare(String(bVal), 'sv');
      }

      return this.sortAscending ? comparison : -comparison;
    });

    this.sortAscending = !this.sortAscending;
    this.reportData = [...this.reportData];
  }

  getCellValue(item: ProjectReportItem, header: HeaderColumn): string | number | null {
    if (header.key === 'customername') {
      const cust = item.customername ?? '';
      const end = item.endcustomer;
      return end ? `${end} (${cust})` : cust;
    }
    return item[header.key as keyof ProjectReportItem] ?? null;
  }

  exportToExcel(): void {
    const exportData = this.reportData.map((item) => {
      const row: Record<string, string | number> = {};
      for (const header of this.headers) {
        const value = this.getCellValue(item, header);
        row[header.label] = value ?? '';
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    removeBlankStringCells(worksheet);
    applyWorksheetFormats(worksheet, this.headers, exportData.length);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Projektrapport');
    XLSX.writeFile(workbook, 'projreport.xlsx');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
